import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  useAssetTypesCatalog,
  useCreateInstance,
  useInstances,
  useProject,
} from '../hooks/useApi';
import type { AssetCategory, AssetSubtype, InstanceKind } from '../api/types';

const KIND_LABELS: Record<InstanceKind, string> = {
  image_360: '360 equirectangular',
  image_2d: 'Imagen 2D',
  video: 'Video',
  avatar: 'Avatar parlante',
  audio: 'Audio / TTS',
  three_d: 'Modelo 3D',
  mixed: 'Mixto',
};

export function ProjectPage() {
  const { projectSlug } = useParams();
  const { data: project } = useProject(projectSlug);
  const { data: instances } = useInstances(projectSlug);
  const [showCreate, setShowCreate] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Discoverabilidad desde el Sidebar: el botón ＋ del ProjectItem linkea a
  // `/projects/<slug>?create=1` para abrir el wizard sin click extra.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreate(true);
      // Limpiar el query param para que un refresh no reabra el modal.
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <Link to="/projects" className="text-sm text-slate-400 hover:text-slate-200">
          ← Proyectos
        </Link>
        <h1 className="text-2xl font-semibold mt-2">{project?.name ?? projectSlug}</h1>
        {project?.client && <div className="text-slate-400">{project.client.name}</div>}
        {project?.owner && (
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <span>👤</span>
            <span>Creado por {project.owner.name || project.owner.email}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg">Assets</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 bg-vision-dark hover:bg-vision rounded-lg text-sm"
        >
          + Nuevo asset
        </button>
      </div>

      <div className="space-y-2">
        {instances?.map((i) => (
          <Link
            key={i.id}
            to={`/projects/${projectSlug}/instances/${i.slug}`}
            className="flex items-center justify-between p-3 bg-slate-900 border border-slate-800 hover:border-vision rounded-lg transition-colors"
          >
            <div>
              <div className="font-medium">{i.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {i.subtype ?? KIND_LABELS[i.kind] ?? i.kind}
                <span className="mx-2">·</span>
                {i.status}
              </div>
            </div>
            <span className="text-vision">→</span>
          </Link>
        ))}
        {instances && instances.length === 0 && (
          <div className="p-4 bg-slate-900 border border-dashed border-slate-700 rounded-lg text-slate-500 text-center">
            Sin assets todavía.
          </div>
        )}
      </div>

      {showCreate && projectSlug && (
        <CreateAssetWizard projectSlug={projectSlug} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

/**
 * Wizard de creación de asset en 2 pasos:
 *  1. Elegir CATEGORÍA (8 cards visuales — personajes, ambientes, video, etc.)
 *  2. Elegir SUB-TIPO (lista detallada con experto + costo + descripción)
 *  3. Nombre + Crear → backend crea Instance con kind+subtype y abre chat con el experto correcto
 */
function CreateAssetWizard({
  projectSlug,
  onClose,
}: {
  projectSlug: string;
  onClose: () => void;
}) {
  const { data: catalog, isLoading } = useAssetTypesCatalog();
  const create = useCreateInstance(projectSlug);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<AssetSubtype | null>(null);
  const [name, setName] = useState('');

  const subtypesForCategory = useMemo(() => {
    if (!catalog || !selectedCategory) return [];
    return catalog.subtypes.filter((s) => s.category === selectedCategory);
  }, [catalog, selectedCategory]);

  const submit = async () => {
    if (!selectedSubtype || !name.trim()) return;
    await create.mutateAsync({
      name: name.trim(),
      kind: selectedSubtype.instanceKind,
      subtype: selectedSubtype.key,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">+ Nuevo asset</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {!selectedCategory
                ? 'Paso 1: elegí la categoría'
                : !selectedSubtype
                  ? `Paso 2: elegí el sub-tipo en ${
                      catalog?.categories.find((c) => c.key === selectedCategory)?.label ?? selectedCategory
                    }`
                  : 'Paso 3: nombre y crear'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(selectedCategory || selectedSubtype) && (
              <button
                onClick={() => {
                  if (selectedSubtype) setSelectedSubtype(null);
                  else setSelectedCategory(null);
                }}
                className="text-xs text-slate-400 hover:text-slate-200"
              >
                ← atrás
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-lg leading-none">
              ✕
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="text-slate-400 text-sm text-center py-8">Cargando catálogo…</div>
          )}

          {/* Paso 1: categorías */}
          {catalog && !selectedCategory && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {catalog.categories.map((c) => (
                <CategoryCard
                  key={c.key}
                  category={c}
                  count={catalog.subtypes.filter((s) => s.category === c.key).length}
                  onClick={() => setSelectedCategory(c.key)}
                />
              ))}
            </div>
          )}

          {/* Paso 2: sub-tipos de la categoría */}
          {catalog && selectedCategory && !selectedSubtype && (
            <div className="space-y-2">
              {subtypesForCategory.map((s) => (
                <SubtypeCard
                  key={s.key}
                  subtype={s}
                  onClick={() => {
                    if (s.comingSoon) return;
                    setSelectedSubtype(s);
                    if (!name) setName(s.label);
                  }}
                />
              ))}
            </div>
          )}

          {/* Paso 3: nombre + confirmar */}
          {selectedSubtype && (
            <div className="space-y-4 max-w-xl mx-auto py-2">
              <div className="bg-slate-950 border border-slate-700 rounded-lg p-4 flex items-start gap-3">
                <span className="text-3xl">{selectedSubtype.emoji}</span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-100">{selectedSubtype.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{selectedSubtype.description}</div>
                  <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
                    <span className="text-vision">🤖 {selectedSubtype.expert}</span>
                    {selectedSubtype.estimatedCost && (
                      <span className="text-amber-400">💰 {selectedSubtype.estimatedCost}</span>
                    )}
                    {selectedSubtype.defaultModel && (
                      <span className="text-slate-500">⚙ {selectedSubtype.defaultModel}</span>
                    )}
                    {selectedSubtype.isAdvancedPipeline && (
                      <span className="text-purple-400">⭐ pipeline avanzado</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 block mb-1">Nombre del asset</label>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ej. 360 Recepción / Personaje principal / etc."
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-sm focus:outline-none focus:border-vision"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && name.trim()) submit();
                  }}
                />
              </div>

              {create.error && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
                  {(create.error as Error).message}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={submit}
                  disabled={create.isPending || !name.trim()}
                  className="px-4 py-2 text-sm bg-vision-dark hover:bg-vision rounded-lg disabled:opacity-40"
                >
                  {create.isPending ? 'Creando…' : `Crear y abrir chat con ${selectedSubtype.expert}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryCard({
  category,
  count,
  onClick,
}: {
  category: AssetCategory;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-slate-950 border border-slate-700 hover:border-vision rounded-lg p-4 text-left transition-colors flex flex-col gap-1.5"
    >
      <div className="flex items-center justify-between">
        <span className="text-3xl">{category.emoji}</span>
        <span className="text-[10px] text-slate-500">{count} sub-tipos</span>
      </div>
      <div className="text-sm font-semibold text-slate-100">{category.label}</div>
      <div className="text-[10px] text-slate-500 line-clamp-2">{category.description}</div>
    </button>
  );
}

function SubtypeCard({
  subtype,
  onClick,
}: {
  subtype: AssetSubtype;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={subtype.comingSoon}
      className={`w-full text-left p-3 rounded-lg border transition-colors flex items-start gap-3 ${
        subtype.comingSoon
          ? 'bg-slate-950 border-slate-800 opacity-50 cursor-not-allowed'
          : 'bg-slate-950 border-slate-700 hover:border-vision hover:bg-slate-900'
      }`}
    >
      <span className="text-2xl shrink-0">{subtype.emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-100">{subtype.label}</span>
          {subtype.isAdvancedPipeline && (
            <span className="text-[10px] text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded px-1.5 py-0.5">
              ⭐ avanzado
            </span>
          )}
          {subtype.comingSoon && (
            <span className="text-[10px] text-slate-400 bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5">
              próximamente
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">{subtype.description}</div>
        <div className="flex flex-wrap gap-3 mt-1.5 text-[10px]">
          <span className="text-vision/80">🤖 {subtype.expert}</span>
          {subtype.estimatedCost && (
            <span className="text-amber-400/80">💰 {subtype.estimatedCost}</span>
          )}
          {subtype.defaultModel && !subtype.isAdvancedPipeline && (
            <span className="text-slate-500 truncate max-w-xs">⚙ {subtype.defaultModel}</span>
          )}
        </div>
      </div>
    </button>
  );
}
